/**
 * CMA Report Panel Component
 * 
 * KOPS-384: Created on February 16, 2026
 * 
 * Purpose: Enhanced visual panel for CMA reports with map and statistics
 * Shows property location on map with market analysis below
 */

import React, { useEffect, useState, useRef } from 'react';
import { Card, Row, Col, ProgressBar, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faChartLine, faClock, faDollarSign, faMapMarkedAlt } from '@fortawesome/free-solid-svg-icons';
import Map, { Marker, NavigationControl, Source, Layer } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import axiosInstance from '../../helpers/api';

interface CMAData {
  property: {
    address: string;
    bedrooms: number;
    bathrooms: number;
    living_area: number;
    year_built: number;
    property_type: string;
    latitude?: number;
    longitude?: number;
  };
  valuation: {
    estimated_value: number;
    confidence_score: number;
    price_range: {
      min: number;
      max: number;
    };
  };
  comparable_count: number;
  market_analysis: {
    analysis_type: string;
    search_radius: number;
    generated_at: string;
  };
  market_stats?: {
    average_sale_price?: number;
    median_sale_price?: number;
    average_days_on_market?: number;
    recent_sales_count?: number;
    price_trend?: string;
  };
  comparables?: any[];
}

interface CMAReportPanelProps {
  cmaData: { report: CMAData };
}

// Circle layer style for search radius
const searchRadiusLayer = {
  id: 'search-radius',
  type: 'fill' as const,
  paint: {
    'fill-color': '#3498db',
    'fill-opacity': 0.1,
  },
};

const searchRadiusBorderLayer = {
  id: 'search-radius-border',
  type: 'line' as const,
  paint: {
    'line-color': '#3498db',
    'line-width': 2,
    'line-dasharray': [2, 4],
  },
};

const CMAReportPanel: React.FC<CMAReportPanelProps> = ({ cmaData }) => {
  const report = cmaData.report;
  const [mapboxToken, setMapboxToken] = useState<string>('');
  const [tokenLoaded, setTokenLoaded] = useState(false);
  const [coordinates, setCoordinates] = useState({
    latitude: report.property.latitude || 33.4484,
    longitude: report.property.longitude || -112.0740,
  });
  const [viewState, setViewState] = useState({
    longitude: coordinates.longitude,
    latitude: coordinates.latitude,
    zoom: 14,
  });
  
  // Fetch Mapbox token from API
  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const response = await axiosInstance.get('/properties/config/mapbox');
        if (response.data.mapboxToken) {
          setMapboxToken(response.data.mapboxToken);
          console.log('✅ Mapbox token loaded from API for CMA panel');
          setTokenLoaded(true);
        } else {
          throw new Error('No token in response');
        }
      } catch (error) {
        console.error('❌ Failed to fetch Mapbox token:', error);
        // Try fallback token
        const fallbackToken = 'pk.eyJ1IjoiaG91c2VleiIsImEiOiJjbHQyZ2dyMm0wMGxxMmxuczR5dmlpOWxtIn0.s-vyWzYxKcc2c7ObD5gvqQ';
        setMapboxToken(fallbackToken);
        console.log('⚠️ Using fallback Mapbox token');
        setTokenLoaded(true);
      }
    };

    fetchMapboxToken();
  }, []);
  
  // Geocode address if coordinates not available
  useEffect(() => {
    const geocodeAddress = async () => {
      if (!report.property.latitude || !report.property.longitude) {
        if (mapboxToken && report.property.address) {
          try {
            // Add city and state for better geocoding accuracy (Phoenix, AZ default)
            const fullAddress = `${report.property.address}, Phoenix, AZ`;
            const encodedAddress = encodeURIComponent(fullAddress);
            const geocodeUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxToken}&limit=1`;
            
            const response = await fetch(geocodeUrl);
            const data = await response.json();
            
            if (data.features && data.features.length > 0) {
              const [lng, lat] = data.features[0].center;
              console.log('📍 Geocoded address:', report.property.address, '→', lat, lng);
              setCoordinates({ latitude: lat, longitude: lng });
              setViewState(prev => ({ ...prev, latitude: lat, longitude: lng }));
            }
          } catch (error) {
            console.error('❌ Failed to geocode address:', error);
          }
        }
      }
    };

    if (tokenLoaded && mapboxToken) {
      geocodeAddress();
    }
  }, [tokenLoaded, mapboxToken, report.property]);
  
  // Calculate confidence color
  const confidenceColor = report.valuation.confidence_score >= 0.85 ? 'success' : 
                         report.valuation.confidence_score >= 0.7 ? 'warning' : 'danger';
  
  // Price trend indicator
  const trendIcon = report.market_stats?.price_trend === 'increasing' ? '📈' : 
                    report.market_stats?.price_trend === 'decreasing' ? '📉' : '➡️';

  // Create circle data for search radius
  const createCircle = (center: [number, number], radiusMiles: number, points = 64) => {
    const radiusKm = radiusMiles * 1.60934;
    const coordinates = [];
    
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * (2 * Math.PI);
      const latitude = center[1] + (radiusKm / 111) * Math.cos(angle);
      const longitude = center[0] + (radiusKm / (111 * Math.cos(center[1] * Math.PI / 180))) * Math.sin(angle);
      coordinates.push([longitude, latitude]);
    }
    coordinates.push(coordinates[0]); // Close the circle
    
    return {
      type: 'Feature' as const,
      geometry: {
        type: 'Polygon' as const,
        coordinates: [coordinates],
      },
      properties: {},
    };
  };

  const searchRadiusData = createCircle([coordinates.longitude, coordinates.latitude], report.market_analysis.search_radius);

  return (
    <div className="d-flex flex-column h-100">
      {/* Map Section - Top Half */}
      <div style={{ height: '45%', position: 'relative' }}>
        <Card className="h-100 border-0 rounded-0">
          <Card.Body className="p-0">
            {tokenLoaded && mapboxToken ? (
              <Map
                {...viewState}
                onMove={evt => setViewState(evt.viewState)}
                mapStyle="mapbox://styles/mapbox/light-v11"
                mapboxAccessToken={mapboxToken}
                style={{ width: '100%', height: '100%' }}
                interactive={true}
                scrollZoom={false}
              >
                <NavigationControl position="top-right" />
                
                {/* Search Radius Circle */}
                <Source type="geojson" data={searchRadiusData}>
                  <Layer {...searchRadiusLayer} />
                  <Layer {...searchRadiusBorderLayer} />
                </Source>
                
                {/* Subject Property Marker */}
                <Marker 
                  longitude={coordinates.longitude} 
                  latitude={coordinates.latitude}
                  anchor="bottom"
                >
                  <div style={{
                    background: '#e74c3c',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '3px solid white',
                    boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '20px',
                    cursor: 'pointer'
                  }}>
                    🏠
                  </div>
                </Marker>
              </Map>
            ) : (
              // Loading or fallback when no Mapbox token
              <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                <div className="text-center">
                  {!tokenLoaded ? (
                    <>
                      <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading map...</span>
                      </div>
                      <p className="text-muted">Loading map...</p>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faMapMarkedAlt} size="3x" className="text-muted mb-3" />
                      <h5 className="text-muted">{report.property.address}</h5>
                      <p className="text-muted">Map view unavailable</p>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Map Overlay Info */}
            <div style={{ 
              position: 'absolute', 
              top: '10px', 
              left: '10px', 
              zIndex: 1000,
              background: 'rgba(255,255,255,0.9)',
              padding: '8px 12px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <small className="text-muted">
                <FontAwesomeIcon icon={faMapMarkedAlt} className="me-1" />
                {report.market_analysis.search_radius} mile radius • {report.comparable_count} comparables
              </small>
            </div>
          </Card.Body>
        </Card>
      </div>
      
      {/* Statistics Section - Bottom Half */}
      <div style={{ height: '55%', overflow: 'auto' }} className="p-3">
        {/* Valuation Card */}
        <Card className="mb-3 border-0 shadow-sm">
          <Card.Body>
            <h6 className="text-muted mb-3">
              <FontAwesomeIcon icon={faDollarSign} className="me-2" />
              AI Valuation
            </h6>
            <h3 className="text-primary mb-2">
              {report.valuation.estimated_value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </h3>
            <div className="mb-3">
              <small className="text-muted d-block mb-1">Price Range</small>
              <div className="d-flex align-items-center">
                <span className="small">
                  {report.valuation.price_range.min.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </span>
                <div className="flex-grow-1 mx-2">
                  <ProgressBar 
                    now={50} 
                    variant="primary" 
                    style={{ height: '6px' }}
                  />
                </div>
                <span className="small">
                  {report.valuation.price_range.max.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </span>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span className="small text-muted me-2">Confidence Score:</span>
              <Badge bg={confidenceColor}>
                {Math.round(report.valuation.confidence_score * 100)}%
              </Badge>
            </div>
          </Card.Body>
        </Card>
        
        {/* Market Statistics */}
        {report.market_stats && (
          <Card className="mb-3 border-0 shadow-sm">
            <Card.Body>
              <h6 className="text-muted mb-3">
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                Market Statistics
              </h6>
              <Row className="g-2">
                <Col xs={6}>
                  <div className="text-center p-2 bg-light rounded">
                    <small className="text-muted d-block">Avg Sale Price</small>
                    <strong className="text-primary">
                      {(report.market_stats.average_sale_price || 0).toLocaleString('en-US', { 
                        style: 'currency', 
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </strong>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="text-center p-2 bg-light rounded">
                    <small className="text-muted d-block">Days on Market</small>
                    <strong className="text-primary">
                      {report.market_stats.average_days_on_market || 'N/A'}
                    </strong>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="text-center p-2 bg-light rounded">
                    <small className="text-muted d-block">Recent Sales</small>
                    <strong className="text-primary">
                      {report.market_stats.recent_sales_count || 0}
                    </strong>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="text-center p-2 bg-light rounded">
                    <small className="text-muted d-block">Price Trend</small>
                    <strong className="text-primary">
                      {trendIcon} {report.market_stats.price_trend || 'Stable'}
                    </strong>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
        
        {/* Property Details */}
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <h6 className="text-muted mb-3">
              <FontAwesomeIcon icon={faHome} className="me-2" />
              Property Details
            </h6>
            <Row className="g-2 small">
              <Col xs={12}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Type:</span>
                  <strong>{report.property.property_type}</strong>
                </div>
              </Col>
              <Col xs={12}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Size:</span>
                  <strong>{report.property.living_area?.toLocaleString()} sqft</strong>
                </div>
              </Col>
              <Col xs={12}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Beds/Baths:</span>
                  <strong>{report.property.bedrooms} / {report.property.bathrooms}</strong>
                </div>
              </Col>
              <Col xs={12}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Year Built:</span>
                  <strong>{report.property.year_built}</strong>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        {/* Analysis Timestamp */}
        <div className="text-center mt-3">
          <small className="text-muted">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            Analysis generated {new Date(report.market_analysis.generated_at || Date.now()).toLocaleDateString()}
          </small>
        </div>
      </div>
    </div>
  );
};

export default CMAReportPanel;